import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { identifyUser, resetIdentity, track, Events } from '../services/analytics';
import { isAgencyPaid } from '../utils/agencyPlans';
import { apiFetch } from '../services/api';

// These will be replaced with real values from .env
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, { auth: { flowType: 'pkce', detectSessionInUrl: true, persistSession: true, autoRefreshToken: true } });

// Strip OAuth-callback artefacts from the URL so refreshes / back navigation
// don't replay the auth flow. Handles both PKCE (`?code=...`) and the older
// implicit flow (`#access_token=...`). Preserves any unrelated query params.
function cleanupOAuthUrl() {
  const params = new URLSearchParams(window.location.search);
  let dirty = false;
  ['code', 'state', 'error', 'error_description', 'error_code'].forEach(p => {
    if (params.has(p)) { params.delete(p); dirty = true; }
  });
  const hasOAuthHash = window.location.hash.includes('access_token') ||
                       window.location.hash.includes('error');
  if (dirty || hasOAuthHash) {
    const search = params.toString();
    const newUrl = window.location.pathname + (search ? '?' + search : '');
    window.history.replaceState(null, '', newUrl);
  }
}

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [org,     setOrg]     = useState(null); // { id, name, role, owner_user_id } when user is part of an active agency
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Strip leftover hash fragments from the older implicit OAuth flow.
    // We do NOT strip `?code=` here — Supabase's PKCE handler needs it to
    // exchange for a session. Cleaning that happens after SIGNED_IN fires.
    if (window.location.hash.includes('access_token') ||
        window.location.hash.includes('error')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        if (event === 'SIGNED_IN') {
          // Now that the code has been exchanged, it's safe to drop it from
          // the URL and push a history sponge so the first back-press stays
          // on the app instead of returning to Google's OAuth page.
          cleanupOAuthUrl();
          if (!window.history.state || !window.history.state.__propsightSponge) {
            window.history.pushState({ __propsightSponge: true }, '', window.location.href);
          }
        }
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Refresh profile when tab becomes visible (picks up webhook updates)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && user) {
        fetchProfile(user.id);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user]);

  // Post-Stripe-Checkout polling. The user lands back at /?upgraded=true
  // in parallel with the webhook firing on our server, so the profile may
  // still show the pre-upgrade plan on first render. Poll briefly until
  // the subscription_status reflects the upgrade, then strip the query
  // params so a future refresh doesn't re-trigger.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') !== 'true') return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;          // 10 × 1500ms = 15s ceiling
    const beforeStatus = profile?.subscription_status || null;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      await fetchProfile(user.id);
      // Re-read freshly fetched state via a microtask; we approximate by
      // refetching a single row and comparing.
      const { data: fresh } = await supabase
        .from('profiles')
        .select('subscription_status')
        .eq('id', user.id)
        .single();
      const changed = fresh?.subscription_status && fresh.subscription_status !== beforeStatus;
      if (changed || attempts >= MAX_ATTEMPTS) {
        const url = new URL(window.location.href);
        url.searchParams.delete('upgraded');
        url.searchParams.delete('session_id');
        window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
        return;
      }
      setTimeout(tick, 1500);
    };
    setTimeout(tick, 800); // First poke after Stripe's typical webhook lag.
    return () => { cancelled = true; };
  }, [user]);

  async function fetchProfile(userId) {
    let { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    // Set role from landing page selection if first login
    const intendedRole = sessionStorage.getItem('intended_role');
    if (intendedRole && (!data?.role || data?.role === 'investor')) {
      await supabase.from('profiles').update({ role: intendedRole }).eq('id', userId);
      sessionStorage.removeItem('intended_role');
      data = { ...data, role: intendedRole };
    }

    // Org membership — if the user belongs to an active agency, surface
    // it as `org`. Synthesise broker access ONLY when the user hasn't
    // explicitly chosen the investor portal (role='investor' means they
    // clicked "Switch to Investor" and want that view, even though they're
    // also part of an agency).
    let orgRow = null;
    try {
      const { data: m } = await supabase
        .from('org_members')
        .select('role, organizations (id, name, owner_user_id, status, plan, seat_limit, lead_routing, lead_rotation_index)')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();
      if (m?.organizations?.status === 'active') {
        orgRow = {
          id: m.organizations.id,
          name: m.organizations.name,
          owner_user_id: m.organizations.owner_user_id,
          status: m.organizations.status,
          plan: m.organizations.plan,
          seat_limit: m.organizations.seat_limit,
          lead_routing: m.organizations.lead_routing || 'direct',
          member_role: m.role,
          is_owner: m.organizations.owner_user_id === userId,
        };
        // Respect explicit investor choice; otherwise grant broker access
        // through the agency.
        if (data?.role !== 'investor') {
          data = { ...data, role: 'broker', broker_verified: true };
        }
      }
    } catch { /* org tables may not exist yet — silent */ }

    setProfile(data);
    setOrg(orgRow);
    setLoading(false);

    // Wire the loaded profile to PostHog so events are attributed to the
    // right user across sessions and devices.
    try {
      const userObj = (await supabase.auth.getUser()).data?.user;
      if (userObj) identifyUser(userObj, data);
    } catch { /* silent — analytics never blocks auth */ }
  }

  async function signInWithGoogle() {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  }

  // UAE Pass is bridged through a Vercel function (not a built-in Supabase
  // OAuth provider), so we full-page-redirect to /api/uaepass/start. The
  // function eventually issues a Supabase magic link back to the SPA and
  // the existing onAuthStateChange listener picks the session up.
  function signInWithUaePass() {
    const next = window.location.pathname + window.location.search;
    const ui = (lang === 'ar' ? 'ar' : 'en');
    window.location.href = `/api/uaepass/start?ui=${ui}&next=${encodeURIComponent(next)}`;
  }

  async function signInWithEmail(email, password) {
    return supabase.auth.signInWithPassword({ email, password });
  }

  async function signUpWithEmail(email, password, fullName) {
    const res = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    if (!res.error && res.data?.user) {
      // Stitch the anonymous landing-page funnel to the new user_id before
      // firing the completion event, so SIGNUP_COMPLETED is attributed to
      // the identified user, not the anonymous distinct_id.
      identifyUser(res.data.user, null);
      track(Events.SIGNUP_COMPLETED, { method: 'email' });
    }
    return res;
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    try { resetIdentity(); } catch { /* silent */ }
  }

  // Active agency membership grants full Pro/Agency access regardless of
  // the individual `subscription_status` — owners *and* invited members
  // both qualify, since the AgencyApp is gated on this same predicate.
  // Fold it into isPro/isLite so every existing feature gate (sidebar
  // locks, paywall screens, plan badges) auto-respects agency access
  // without each call site needing to know about agencies.
  const isAgency = isAgencyPaid(org);
  const isPro = isAgency || profile?.subscription_status === 'pro' || profile?.subscription_status === 'agent';
  const isLite = isAgency || profile?.subscription_status === 'lite' || isPro;
  const isAgent = profile?.subscription_status === 'agent';
  // Shared public demo account flag. Disables agency / CRM affordances
  // in the sidebar and triggers per-IP AI quotas server-side. The email
  // fallback covers the (common) case where the auth user exists but
  // the profile-row `is_demo` column hasn't been backfilled yet — keeps
  // the demo UX coherent without requiring the SQL update first.
  const DEMO_EMAILS = new Set(['demo@thepropsight.com']);
  const isDemo = !!profile?.is_demo || (user?.email ? DEMO_EMAILS.has(user.email.toLowerCase()) : false);

  // Demo browser-trial state — fetched on demo sign-in so the expired
  // overlay can render before the visitor even touches an AI feature.
  // Trial is keyed on a UUID we generate the first time a demo visitor
  // lands here; that UUID lives in localStorage and is auto-attached
  // by apiFetch as the `x-demo-trial` header on every backend call.
  // This makes the countdown stable across IP changes within the same
  // browser (UAE mobile / VPN visitors were getting fresh 14-day
  // trials on every session under the old per-IP scheme).
  // null = not yet fetched; { expired, expires_at, first_seen_at, not_started, ms_remaining } once loaded.
  const [demoTrial, setDemoTrial] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!isDemo || !user) { setDemoTrial(null); return; }
    // Ensure a stable browser-level trial ID exists BEFORE the status
    // fetch — apiFetch reads it from localStorage at request time.
    try {
      if (!localStorage.getItem('propsight:demo_trial_id')) {
        const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `dt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('propsight:demo_trial_id', uuid);
      }
    } catch { /* private mode / disabled storage — skip; backend will treat as no trial id */ }
    (async () => {
      try {
        const res = await apiFetch('/api/claude?_action=demo_status');
        const data = await res.json();
        if (!cancelled) setDemoTrial(data?.trial || null);
      } catch (e) {
        // Don't hard-fail — overlay just won't render, the AI endpoint
        // will still reject expired calls.
        if (!cancelled) setDemoTrial(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isDemo, user?.id]);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const toggleTheme = () => { const n=theme==='dark'?'light':'dark'; setTheme(n); localStorage.setItem('theme',n); };
  const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');
  const toggleLang = () => { const n=lang==='en'?'ar':'en'; setLang(n); localStorage.setItem('lang',n); };
  // Sync the document direction with lang so RTL applies to *every* portal,
  // not just the broker app. theme.css already styles `:root[dir="rtl"]`
  // (Cairo font, letter-spacing reset), so flipping the html dir is enough
  // to cascade Arabic typography across the whole tree.
  useEffect(() => {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  // Quota is enforced server-side by the record_search() Postgres function.
  // canSearch is a UX hint based on the cached profile; the authoritative
  // allow/deny comes from recordSearch's RPC response.
  const canSearch = () => {
    if (isPro) return true;
    const today = new Date().toISOString().split('T')[0];
    const resetDate = profile?.searches_reset_at;
    const count = resetDate === today ? (profile?.searches_today || 0) : 0;
    return count < 3;
  };

  const recordSearch = async () => {
    if (!user || isPro) return { allowed: true, remaining: null };
    const { data, error } = await supabase.rpc('record_search');
    if (error) {
      console.error('record_search rpc:', error);
      return { allowed: false, remaining: 0 };
    }
    // Refresh profile so canSearch reflects the new count.
    fetchProfile(user.id);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user, profile, org, loading,
      isPro, isLite, isAgent, isAgency, isDemo, demoTrial, canSearch, recordSearch,
      signInWithGoogle, signInWithUaePass, signInWithEmail, signUpWithEmail, signOut,
      refreshProfile: () => user && fetchProfile(user.id),
      theme, toggleTheme, lang, toggleLang,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
